import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ArrowUp,
  ArrowDown,
  X,
  MoreVertical,
  Filter,
  FilterX,
  Pin,
} from "lucide-react";
import { MultiSelectCombobox } from "@/components/MultiSelectCombobox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { cn } from "@/lib/utils";

export type SortDirection = "asc" | "desc" | null;

export type FilterType =
  | "text"
  | "select"
  | "multi-select"
  | "date-range"
  | "number"
  | "boolean";

export type TextFilterMode = "contains" | "equals" | "isEmpty";

export interface FilterConfig {
  type: FilterType;
  options?: string[] | { label: string; value: string }[];
  placeholder?: string;
}

export interface ColumnFilter {
  value?: string | number | boolean | string[] | null;
  mode?: TextFilterMode;
  dateFrom?: Date;
  dateTo?: Date;
  numberFrom?: number;
  numberTo?: number;
}

interface TableColumnHeaderProps {
  title: string;
  columnKey?: string;
  sortable?: boolean;
  filterable?: boolean;
  filterConfig?: FilterConfig;
  sortDirection?: SortDirection;
  onSort?: () => void;
  filter?: ColumnFilter;
  onFilterChange?: (filter: ColumnFilter | null) => void;
  isPinnedLeft?: boolean;
  isPinnedRight?: boolean;
  onPinLeft?: () => void;
  onPinRight?: () => void;
}

export function TableColumnHeader({
  title,
  sortable = false,
  filterable = false,
  filterConfig,
  sortDirection = null,
  onSort,
  filter,
  onFilterChange,
  isPinnedLeft = false,
  isPinnedRight = false,
  onPinLeft,
  onPinRight,
}: TableColumnHeaderProps) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [localFilter, setLocalFilter] = useState<ColumnFilter>(filter || {});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [filterPanelPosition, setFilterPanelPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    setLocalFilter(filter || {});
  }, [filter]);

  useEffect(() => {
    if (isFilterOpen && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      const panelWidth = 340;
      const panelHeight = 400;

      let top = rect.bottom + 8;
      let left = rect.left;

      // Clamp to right edge of viewport
      if (left + panelWidth > window.innerWidth) {
        left = window.innerWidth - panelWidth - 16;
      }
      // Clamp to left edge
      if (left < 8) {
        left = 8;
      }
      // If panel would overflow bottom, position above the button
      if (top + panelHeight > window.innerHeight) {
        top = rect.top - panelHeight - 8;
        if (top < 8) top = 8;
      }

      setFilterPanelPosition({ top, left });
    }
  }, [isFilterOpen]);

  const hasActiveFilter = filter && (
    filter.mode === "isEmpty" ||
    (filter.value !== undefined && filter.value !== null && filter.value !== "" &&
     (Array.isArray(filter.value) ? filter.value.length > 0 : true)) ||
    filter.dateFrom !== undefined ||
    filter.dateTo !== undefined ||
    (filter.numberFrom !== undefined && filter.numberFrom !== null) ||
    (filter.numberTo !== undefined && filter.numberTo !== null)
  );

  const handleApplyFilter = () => {
    if (onFilterChange) {
      const hasValues =
        localFilter.mode === "isEmpty" ||
        (localFilter.value !== undefined && localFilter.value !== null && localFilter.value !== "" &&
         (Array.isArray(localFilter.value) ? localFilter.value.length > 0 : true)) ||
        localFilter.dateFrom !== undefined ||
        localFilter.dateTo !== undefined ||
        localFilter.numberFrom !== undefined ||
        localFilter.numberTo !== undefined;

      onFilterChange(hasValues ? localFilter : null);
    }
    setIsFilterOpen(false);
  };

  const handleClearFilter = () => {
    setLocalFilter({});
    if (onFilterChange) {
      onFilterChange(null);
    }
    setIsFilterOpen(false);
  };

  const handlePinLeft = () => {
    if (onPinLeft) onPinLeft();
    setIsDropdownOpen(false);
  };

  const handlePinRight = () => {
    if (onPinRight) onPinRight();
    setIsDropdownOpen(false);
  };

  const renderFilterContent = () => {
    if (!filterConfig) return null;

    switch (filterConfig.type) {
      case "text":
        return (
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium block">Filter Mode</Label>
              <Select
                value={localFilter.mode || "contains"}
                onValueChange={(value: TextFilterMode) => {
                  const update: Partial<ColumnFilter> = { ...localFilter, mode: value };
                  if (value === "isEmpty") {
                    update.value = undefined;
                  }
                  setLocalFilter(update as ColumnFilter);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[10000]">
                  <SelectItem value="contains">Contains</SelectItem>
                  <SelectItem value="equals">Equals</SelectItem>
                  <SelectItem value="isEmpty">Is Empty</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {localFilter.mode !== "isEmpty" && (
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium block">Value</Label>
                <Input
                  placeholder={filterConfig.placeholder || "Enter value..."}
                  value={(localFilter.value as string) || ""}
                  onChange={(e) =>
                    setLocalFilter({ ...localFilter, value: e.target.value })
                  }
                />
              </div>
            )}
          </div>
        );

      case "select":
        return (
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium block">Select Value</Label>
            <Select
              value={(localFilter.value as string) || ""}
              onValueChange={(value) =>
                setLocalFilter({ ...localFilter, value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={filterConfig.placeholder || "Select..."} />
              </SelectTrigger>
              <SelectContent className="z-[10000]">
                {filterConfig.options?.map((option) => {
                  const opt = typeof option === 'string' ? { label: option, value: option } : option;
                  return (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        );

      case "multi-select":
        return (
          <div className="flex flex-col gap-2">
            <MultiSelectCombobox
              options={filterConfig.options?.map(opt =>
                typeof opt === 'string' ? opt : opt.value
              ) || []}
              selected={(localFilter.value as string[]) || []}
              onChange={(selected) =>
                setLocalFilter({ ...localFilter, value: selected })
              }
              placeholder={filterConfig.placeholder || "Select..."}
            />
          </div>
        );

      case "date-range":
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium block">From Date</Label>
              <DatePicker
                value={localFilter.dateFrom}
                onChange={(date) =>
                  setLocalFilter({ ...localFilter, dateFrom: date ?? undefined })
                }
                placeholder="Pick a date"
                popoverClassName="z-[10060]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium block">To Date</Label>
              <DatePicker
                value={localFilter.dateTo}
                onChange={(date) =>
                  setLocalFilter({ ...localFilter, dateTo: date ?? undefined })
                }
                placeholder="Pick a date"
                popoverClassName="z-[10060]"
              />
            </div>
          </div>
        );

      case "number":
        return (
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium block">From</Label>
              <Input
                type="number"
                placeholder="Min value"
                value={localFilter.numberFrom || ""}
                onChange={(e) =>
                  setLocalFilter({
                    ...localFilter,
                    numberFrom: e.target.value ? Number(e.target.value) : undefined,
                  })
                }
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium block">To</Label>
              <Input
                type="number"
                placeholder="Max value"
                value={localFilter.numberTo || ""}
                onChange={(e) =>
                  setLocalFilter({
                    ...localFilter,
                    numberTo: e.target.value ? Number(e.target.value) : undefined,
                  })
                }
              />
            </div>
          </div>
        );

      case "boolean":
        return (
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium block">Select Value</Label>
            <Select
              value={
                localFilter.value === undefined
                  ? ""
                  : localFilter.value
                  ? "true"
                  : "false"
              }
              onValueChange={(value) =>
                setLocalFilter({
                  ...localFilter,
                  value: value === "true",
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent className="z-[10000]">
                <SelectItem value="true">Yes</SelectItem>
                <SelectItem value="false">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );

      default:
        return null;
    }
  };

  const getSortIcon = () => {
    if (sortDirection === "asc") return <ArrowUp className="h-3.5 w-3.5" />;
    if (sortDirection === "desc") return <ArrowDown className="h-3.5 w-3.5" />;
    return null;
  };

  const handleHeaderClick = () => {
    if (sortable && onSort) {
      onSort();
    }
  };

  return (
    <div className="flex items-center justify-between gap-2 relative z-[9999] w-full">
      <div
        className={cn(
          "flex items-center gap-2",
          sortable && "cursor-pointer hover:text-primary transition-colors"
        )}
        onClick={handleHeaderClick}
      >
        <span className="font-medium">{title}</span>
        {sortable && sortDirection && (
          <span className="text-primary">
            {getSortIcon()}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 ml-auto">
        {(filterable || onPinLeft || onPinRight) && (
          <div className="relative z-[9999]">
            <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  ref={menuButtonRef}
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-6 w-6 p-0 transition-all",
                    (hasActiveFilter || isPinnedLeft || isPinnedRight)
                      ? "text-primary bg-primary/10 hover:bg-primary/20"
                      : "text-neutral-400 hover:bg-neutral-100"
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="z-[9999]">
                {filterable && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      setIsDropdownOpen(false);
                      setTimeout(() => setIsFilterOpen(true), 100);
                    }}
                    className="cursor-pointer"
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Filter
                    {hasActiveFilter && (
                      <span className="ml-auto text-xs text-primary">●</span>
                    )}
                  </DropdownMenuItem>
                )}
                {filterable && hasActiveFilter && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      setIsDropdownOpen(false);
                      if (onFilterChange) {
                        onFilterChange(null);
                      }
                      setLocalFilter({});
                    }}
                    className="cursor-pointer"
                  >
                    <FilterX className="h-4 w-4 mr-2" />
                    Clear Filter
                  </DropdownMenuItem>
                )}
                {(onPinLeft || onPinRight) && (
                  <>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.preventDefault();
                        handlePinLeft();
                      }}
                      className="cursor-pointer"
                    >
                      <Pin className={cn("h-4 w-4 mr-2", isPinnedLeft && "fill-current")} />
                      {isPinnedLeft ? "Unpin from Left" : "Pin to Left"}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.preventDefault();
                        handlePinRight();
                      }}
                      className="cursor-pointer"
                    >
                      <Pin className={cn("h-4 w-4 mr-2", isPinnedRight && "fill-current")} />
                      {isPinnedRight ? "Unpin from Right" : "Pin to Right"}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {filterable && isFilterOpen && createPortal(
              <>
                {/* Backdrop overlay to block interaction */}
                <div
                  className="fixed inset-0 z-[9999] bg-black/20"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                />
                <div
                  data-filter-panel
                  className="fixed w-[340px] max-h-[80vh] overflow-y-auto p-4 bg-background border rounded-md shadow-lg z-[10000]"
                  style={{
                    top: `${filterPanelPosition.top}px`,
                    left: `${filterPanelPosition.left}px`,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm">Filter {title}</h4>
                      {hasActiveFilter && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={handleClearFilter}
                        >
                          <X className="h-3 w-3 mr-1" />
                          Clear
                        </Button>
                      )}
                    </div>
                    <div className="space-y-3">
                      {renderFilterContent()}
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={handleApplyFilter}
                      >
                        Apply
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => setIsFilterOpen(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </>,
              document.body
            )}
          </div>
        )}
      </div>
    </div>
  );
}
